import { Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";
import {
  BookmarkIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  EllipsisVerticalIcon,
  TrashIcon,
  ViewfinderCircleIcon,
} from "@heroicons/react/24/outline";
import { BookmarkIcon as BookmarkIconSolid } from "@heroicons/react/24/solid";
import useMyVendorRequestAccess from "../../custom-hooks/useMyVendorRequestAccess";
import MyVendorMembershipMenuItems from "./MyVendorMembershipMenuItems";
import { isMyVendorProfileVisible } from "../../utils/gridOrgNameLinks";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function MenuItemSpinner() {
  return (
    <svg
      className="h-4 w-4 animate-spin text-gray-400"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

/** Renders only when the caller may create that request family (hide unauthorized). */
function RequestNewMenuItem({ family, onAction }) {
  const isVqq = family === "VQQ";
  const Icon = isVqq ? CheckCircleIcon : CheckBadgeIcon;
  const actionId = isVqq ? "request-vqq" : "request-vqa";
  const label = isVqq ? "Request New VQQ" : "Request New VQA";
  const iconClass = isVqq ? "text-indigo-700" : "text-rose-700";

  return (
    <Menu.Item>
      {({ active }) => (
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onAction?.(actionId);
          }}
          className={classNames(
            active ? "bg-gray-50" : "",
            "flex w-full items-center px-3 py-1 text-left text-sm leading-6 text-gray-900"
          )}
        >
          <Icon className={classNames("h-4 w-4", iconClass)} />
          <span className="pl-2">{label}</span>
        </button>
      )}
    </Menu.Item>
  );
}

/** Card/table-level actions menu for My Vendors surfaces. */
export default function MyVendorCardActionMenu({
  vendor,
  onAction,
  onAddToMyVendors,
  onRemoveFromMyVendors,
  onAddToHidden,
  onUnhide,
  isBookmarked = false,
  isBookmarkPending = false,
  isHidePending = false,
  isUnhidePending = false,
  showRemoveFromList = false,
  onRemoveFromList,
  onAddToList,
}) {
  const { canRequestVqq, canRequestVqa } = useMyVendorRequestAccess();
  const isManual = Boolean(vendor?.isManuallyAdded);
  const isHidden = Boolean(vendor?.isHidden);
  // Not to be confused with isHidden (hidden from My Vendors): this is hidden in Discovery.
  const canViewProfile = isMyVendorProfileVisible(vendor);

  const handle = (actionId) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    onAction?.(actionId, vendor);
  };

  const handleRequestAction = (actionId) => {
    onAction?.(actionId, vendor);
  };

  return (
    <Menu as="div" className="relative inline-flex justify-center">
      <Menu.Button className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-400 hover:text-gray-500 focus:outline-none">
        <span className="sr-only">Open options</span>
        <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          anchor="bottom end"
          portal
          className="z-[100] mt-0.5 w-52 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none"
        >
          {canViewProfile ? (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={handle("view-profile")}
                className={classNames(
                  active ? "bg-gray-50" : "",
                  "flex w-full items-center px-3 py-1 text-left text-sm leading-6 text-gray-900"
                )}
              >
                <ViewfinderCircleIcon className="h-4 w-4" />
                <span className="pl-2">View Profile</span>
              </button>
            )}
          </Menu.Item>
          ) : null}
          {canRequestVqq ? (
            <RequestNewMenuItem family="VQQ" onAction={handleRequestAction} />
          ) : null}
          {canRequestVqa ? (
            <RequestNewMenuItem family="VQA" onAction={handleRequestAction} />
          ) : null}
          <Menu.Item disabled={isBookmarkPending}>
            {({ active }) => (
              <button
                type="button"
                onClick={handle("toggle-bookmark")}
                disabled={isBookmarkPending}
                className={classNames(
                  active ? "bg-gray-50" : "",
                  "flex w-full items-center px-3 py-1 text-left text-sm leading-6 text-gray-900 disabled:cursor-wait disabled:opacity-60"
                )}
              >
                {isBookmarkPending ? (
                  <MenuItemSpinner />
                ) : isBookmarked ? (
                  <BookmarkIconSolid className="h-4 w-4 text-indigo-600" />
                ) : (
                  <BookmarkIcon className="h-4 w-4" />
                )}
                <span className="pl-2">
                  {isBookmarkPending
                    ? "Updating…"
                    : isBookmarked
                      ? "Remove Bookmark"
                      : "Bookmark"}
                </span>
              </button>
            )}
          </Menu.Item>
          {onAddToList ? (
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onAddToList(vendor);
                  }}
                  className={classNames(
                    active ? "bg-gray-50" : "",
                    "flex w-full items-center px-3 py-1 text-left text-sm leading-6 text-gray-900"
                  )}
                >
                  <ClipboardDocumentCheckIcon className="h-4 w-4" />
                  <span className="pl-2">Add to List</span>
                </button>
              )}
            </Menu.Item>
          ) : null}
          {showRemoveFromList ? (
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onRemoveFromList?.(vendor);
                  }}
                  className={classNames(
                    active ? "bg-red-50" : "",
                    "flex w-full items-center px-3 py-1 text-left text-sm leading-6 text-red-600"
                  )}
                >
                  <TrashIcon className="h-4 w-4" />
                  <span className="pl-2">Remove from List</span>
                </button>
              )}
            </Menu.Item>
          ) : null}
          <MyVendorMembershipMenuItems
            vendor={vendor}
            isInMyVendors
            isManual={isManual}
            isHidden={isHidden}
            onAddToMyVendors={onAddToMyVendors}
            onRemoveFromMyVendors={
              onRemoveFromMyVendors ||
              (onAction ? (v) => onAction("remove-from-my-vendors", v) : undefined)
            }
            onAddToHidden={
              onAddToHidden || (onAction ? (v) => onAction("add-to-hidden", v) : undefined)
            }
            onUnhide={
              onUnhide || (onAction ? (v) => onAction("unhide", v) : undefined)
            }
            isHidePending={isHidePending}
            isUnhidePending={isUnhidePending}
          />
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
